import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

interface AnimatedOverpixelLogoProps {
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'hero';
  showParticles?: boolean;
  glowIntensity?: 'low' | 'medium' | 'high';
  className?: string;
  /** Remove molduras/overlays e efeitos extras para uso minimalista */
  minimal?: boolean;
  /** Desativa tilt/rotação na animação inicial para não ficar torto */
  tilt?: boolean;
  /** Controla se os textos "OVERPIXEL" e subtítulo abaixo do logo devem aparecer */
  showText?: boolean;
}

const AnimatedOverpixelLogo: React.FC<AnimatedOverpixelLogoProps> = ({
  size = 'md',
  showParticles = true,
  glowIntensity = 'medium',
  className = '',
  minimal = false,
  tilt = true,
  showText = true,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [particles, setParticles] = useState<Array<{ id: number; x: number; y: number; delay: number }>>([]);

  // Configurações de tamanho - FORMATO QUADRADO
  const sizeConfig = {
    sm: { width: 120, height: 120 },
    md: { width: 180, height: 180 },
    lg: { width: 240, height: 240 },
    xl: { width: 320, height: 320 },
    hero: { width: 400, height: 400 }
  };

  // Garantir que o size seja válido
  const currentSize = sizeConfig[size] ? size : 'md';
  const currentSizeConfig = sizeConfig[currentSize];

  // Configurações de glow
  const glowConfig = {
    low: 'drop-shadow(0 0 10px rgba(6, 182, 212, 0.3))',
    medium: 'drop-shadow(0 0 20px rgba(6, 182, 212, 0.5)) drop-shadow(0 0 40px rgba(147, 51, 234, 0.3))',
    high: 'drop-shadow(0 0 30px rgba(6, 182, 212, 0.7)) drop-shadow(0 0 60px rgba(147, 51, 234, 0.5)) drop-shadow(0 0 90px rgba(6, 182, 212, 0.3))'
  };

  // Gerar partículas aleatórias
  useEffect(() => {
    if (showParticles) {
      const newParticles = Array.from({ length: 12 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        delay: Math.random() * 2
      }));
      setParticles(newParticles);
    }
  }, [showParticles]);

  return (
    <div className={`relative inline-block ${className}`}>
      {/* Container principal com animação de entrada */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8, rotateY: tilt ? -30 : 0 }}
        animate={{ opacity: 1, scale: 1, rotateY: 0 }}
        transition={{ 
          duration: 1.2, 
          ease: [0.25, 0.46, 0.45, 0.94],
          delay: 0.2 
        }}
        className="relative"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Background glow effect (opcional) */}
        {!minimal && (
          <motion.div
            className="absolute inset-0 rounded-2xl"
            style={{
              background: 'linear-gradient(45deg, rgba(6, 182, 212, 0.1), rgba(147, 51, 234, 0.1), rgba(6, 182, 212, 0.1))',
              filter: 'blur(20px)',
            }}
            animate={{
              scale: isHovered ? 1.2 : 1,
              opacity: isHovered ? 0.8 : 0.4,
            }}
            transition={{ duration: 0.3 }}
          />
        )}

        {/* Partículas flutuantes */}
        {showParticles && particles.map((particle) => (
          <motion.div
            key={particle.id}
            className="absolute w-1 h-1 bg-cyan-400 rounded-full"
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
            }}
            animate={{
              y: [-10, -30, -10],
              opacity: [0.3, 1, 0.3],
              scale: [0.5, 1, 0.5],
            }}
            transition={{
              duration: 3 + particle.delay,
              repeat: Infinity,
              delay: particle.delay,
              ease: "easeInOut"
            }}
          />
        ))}

        {/* Borda animada (opcional) */}
        {!minimal && (
          <motion.div
            className="absolute inset-0 rounded-2xl border-2"
            style={{
              borderImage: 'linear-gradient(45deg, #06b6d4, #9333ea, #06b6d4) 1',
            }}
            animate={{
              borderColor: isHovered 
                ? ['#06b6d4', '#9333ea', '#06b6d4'] 
                : ['rgba(6, 182, 212, 0.3)', 'rgba(147, 51, 234, 0.3)', 'rgba(6, 182, 212, 0.3)']
            }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        )}

        {/* Logo principal - Substituído por SVG/Texto já que a imagem foi removida */}
        <motion.div
          className="relative z-10 flex items-center justify-center"
          style={{
            width: currentSizeConfig.width,
            height: currentSizeConfig.height,
          }}
          animate={{
            y: isHovered ? -5 : 0,
          }}
          transition={{ duration: 0.3 }}
        >
          <svg viewBox="0 0 200 200" className="w-full h-full">
            <defs>
              <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="5" result="blur" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" />
              </filter>
            </defs>
            <text
              x="50%"
              y="50%"
              dy=".3em"
              textAnchor="middle"
              fill="white"
              fontSize="24"
              fontWeight="bold"
              fontFamily="sans-serif"
              filter="url(#glow)"
            >
              OVERPIXEL
            </text>
          </svg>
        </motion.div>

        {/* Efeito de scan line (opcional) */}
        {!minimal && (
          <motion.div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'linear-gradient(90deg, transparent 0%, rgba(6, 182, 212, 0.1) 50%, transparent 100%)',
            }}
            animate={{
              x: ['-100%', '200%'],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              repeatDelay: 2,
              ease: "easeInOut"
            }}
          />
        )}

        {/* Pulse effect no hover */}
        {!minimal && isHovered && (
          <motion.div
            className="absolute inset-0 rounded-2xl border-2 border-cyan-400"
            initial={{ scale: 1, opacity: 0.8 }}
            animate={{ scale: 1.3, opacity: 0 }}
            transition={{ duration: 0.6, repeat: Infinity }}
          />
        )}
      </motion.div>

      {/* Texto animado opcional abaixo do logo */}
      {showText && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="text-center mt-4"
        >
          <motion.h3
            className="text-lg font-bold bg-gradient-to-r from-cyan-400 via-purple-500 to-cyan-400 bg-clip-text text-transparent"
            animate={{
              backgroundPosition: isHovered ? ['0%', '200%'] : ['0%', '100%'],
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            OVERPIXEL
          </motion.h3>
          <motion.p
            className="text-sm text-gray-400 mt-1"
            animate={{
              opacity: isHovered ? 1 : 0.7,
            }}
          >
            Plataforma Tecnológica para Designers
          </motion.p>
        </motion.div>
      )}
    </div>
  );
};

export default AnimatedOverpixelLogo;